import React, { useState, useRef, useEffect } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import { FaEnvelope } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { userLogin, userLoginVerifyOtp } from "../store/slices/auth";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

export default function Login({ show, handleClose }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [mobileoremail, setMobileorEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (showOtpModal) {
      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 200);
    }
  }, [showOtpModal]);

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleInput = (e) => {
    let value = e.target.value;
    const firstFour = value.substring(0, 4);

    if (/^\d{4}$/.test(firstFour)) {
      value = value.replace(/\D/g, "");
      if (value.length > 10) value = value.slice(0, 10);
      setMobileorEmail(value);
      setEmailError(value.length < 10 ? "Mobile number must be 10 digits" : "");
    } else {
      setMobileorEmail(value);
      setEmailError(
        value.length > 0 && !validateEmail(value)
          ? "Enter valid email address"
          : "",
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mobileoremail) {
      setEmailError("Please enter valid email or mobile number");
      return;
    }
    if (emailError) return;

    try {
      setLoading(true);
      const resultAction = await dispatch(
        userLogin({ username: mobileoremail }),
      ).unwrap();

      const id =
        resultAction?.userId ?? resultAction?.id ?? resultAction?.data?.userId;

      if (!id) throw new Error("User ID not received from server");

      setUserId(id);
      handleClose();
      setShowOtpModal(true);

      toast.success("OTP sent successfully!");
    } catch (error) {
      console.log(error);
      const errorMsg =
        error ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Something went wrong";

      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto submit after all digits entered
    const completedOtp = newOtp.join("");

    if (completedOtp.length === 6 && !newOtp.includes("")) {
      setTimeout(() => {
        verifyOtp(completedOtp);
      }, 150);
    }
  };
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        otpRefs.current[index - 1]?.focus();
      }
    }

    if (e.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();

    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (!pasted) return;

    const newOtp = [...otp];

    pasted.split("").forEach((digit, index) => {
      newOtp[index] = digit;
    });

    setOtp(newOtp);

    if (pasted.length === 6) {
      setTimeout(() => verifyOtp(newOtp.join("")), 150);
    } else {
      otpRefs.current[pasted.length]?.focus();
    }
  };

  const verifyOtp = async (otpValue = otp.join("")) => {
    const enteredOtp = otpValue;

    if (enteredOtp.length !== 6) {
      setOtpError("Please enter the complete 6-digit OTP");
      return;
    }
    setOtpError("");

    try {
      setOtpLoading(true);

      const result = await dispatch(
        userLoginVerifyOtp({ userId, otp: enteredOtp }),
      ).unwrap();
      toast.success("Login successful!");
      setShowOtpModal(false);
      navigate("/user/home");
    } catch (error) {
      const message =
        error ||
        error?.message ||
        error?.data?.message ||
        error?.response?.message ||
        "Invalid OTP. Please try again.";

      toast.error(message);
      setOtpError(message);
    } finally {
      setOtpLoading(false);
    }
  };
  const handleResendOtp = async () => {
    try {
      setLoading(true);
      const resultAction = await dispatch(
        userLogin({ username: mobileoremail, }),
      ).unwrap();
      const id =
        resultAction?.userId ?? resultAction?.id ?? resultAction?.data?.userId;
      if (id) setUserId(id);
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 100);
      setOtpError("");
      toast.success("OTP resent successfully!");
    } catch (error) {
      toast.error(
        error ||
        error?.message ||
        error?.response?.data?.message ||
        "Failed to resend OTP.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* ── LOGIN MODAL ── */}
      <Modal
        show={show}
        onHide={handleClose}
        centered
        size="md"
        backdrop="true"
      >
        <Modal.Body className="login-modal-body">
          <Container fluid className="p-0">
            <Row className="justify-content-center align-items-center m-4">
              <Col
                lg={12}
                md={12}
                sm={12}
                className="d-flex justify-content-center align-items-center my-1 px-1"
              >
                <div className="login-inner">
                  <p className="login-welcome-tag">WELCOME BACK</p>

                  <h2 className="login-title">Sign In to KVL Matrimony</h2>

                  <p className="login-subtitle">
                    Continue your journey to find the perfect match
                  </p>

                  <div className="login-divider">
                    <div className="login-divider-line" />
                    <span className="login-divider-icon">✦</span>
                    <div className="login-divider-line" />
                  </div>

                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="login-form-group">
                          <Form.Label className="login-label">
                            Email / Mobile Number
                          </Form.Label>
                          <div className="login-input-wrapper">
                            <FaEnvelope className="login-input-icon" />
                            <Form.Control
                              type="text"
                              placeholder="Enter Email or Mobile"
                              value={mobileoremail}
                              onChange={handleInput}
                              className="login-input"
                              maxLength={30}
                            />
                          </div>
                          {emailError && (
                            <small className="login-error">{emailError}</small>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    <button
                      type="submit"
                      className="btn-shimmer w-100"
                      disabled={loading}
                    >
                      {loading ? "Sending OTP..." : "Login"}
                    </button>
                  </Form>

                  <p className="login-register-text">
                    New to KVL Matrimony?{" "}
                    <span
                      className="login-register-link"
                      onClick={() => {
                        handleClose();
                        navigate("/register");
                      }}
                    >
                      Create your profile
                    </span>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </Modal.Body>
      </Modal>

      {/* ── OTP MODAL ── */}
      <Modal
        show={showOtpModal}
        onHide={() => setShowOtpModal(false)}
        centered
        size="md"
      >
        <Modal.Body className="login-modal-body">
          <Container fluid className="p-0">
            <Row className="justify-content-center align-items-center m-4">
              <Col md={12} className="text-center">
                <h3 className="otp-title">Verify OTP</h3>

                <p className="otp-subtitle">
                  OTP sent to <strong>{mobileoremail}</strong>
                </p>

                <div className="otp-box-wrapper" onPaste={handleOtpPaste}>
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (otpRefs.current[index] = el)}
                      type="tel"
                      inputMode="numeric"
                      autoComplete={index === 0 ? "one-time-code" : "off"}
                      maxLength={1}
                      disabled={otpLoading}
                      value={digit}
                      onChange={(e) => handleOtpChange(e.target.value, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      className={`otp-box ${digit ? "otp-filled" : ""} ${otpError ? "otp-box--error" : ""
                        }`}
                    />
                  ))}
                </div>

                {otpError && (
                  <small className="login-error d-block mb-2">{otpError}</small>
                )}



                <p className="otp-resend-text">
                  Didn't receive OTP?{" "}
                  <span
                    className="login-register-link"
                    onClick={handleResendOtp}
                  >
                    {loading ? "Resending..." : "Resend OTP"}
                  </span>
                </p>
              </Col>
            </Row>
          </Container>
        </Modal.Body>
      </Modal>
    </>
  );
}
